import { useEffect, useRef, useState } from "react";
import { UploadCloud, X, RefreshCw, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { tx, useLang } from "../../../lib/i18n";
import { loadImage, toCanvas, fmtBytes } from "./ops";
import { Editor, freshSlot } from "./Editor";
import { ResultPanel } from "./ResultPanel";
import { GeneratePanel } from "./AIPanel";
import { LibraryGrid, useLibrary } from "./Library";

const OK = ["image/png", "image/jpeg", "image/webp"];
const status = (s) => (!s ? "Empty" : s.saved && !s.saved.stale ? "Saved" : s.saved ? "Edited since save" : s.stage === "review" ? "Ready to save" : "Editing");

const SlotCard = ({ i, slot, active, onFile, onOpen, onRemove, onGenerate }) => { useLang();
  const ref = useRef(); const [over, setOver] = useState(false);
  const drop = (e) => { e.preventDefault(); setOver(false); const f = e.dataTransfer.files[0]; if (f) onFile(f); };
  return (
    <div onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)} onDrop={drop}
      className={`relative flex min-h-[170px] flex-col rounded-2xl border p-3 transition-colors ${active ? "border-[var(--mv-teal)] bg-[var(--mv-teal-dim)]" : over ? "border-[var(--mv-teal)]" : "border-[var(--mv-border-2)] bg-[var(--mv-card)]"}`} data-testid={`studio-slot-${i}`}>
      <div className="mb-2 flex items-center justify-between text-[11px]">
        <span className="font-bold uppercase tracking-wider text-[var(--mv-muted)]">{tx("Image")} {i + 1}</span>
        <span className={`rounded-full px-2 py-0.5 font-semibold ${slot?.saved && !slot.saved.stale ? "bg-[var(--mv-teal-dim)] text-[var(--mv-teal)]" : "bg-white/5"}`} data-testid={`studio-slot-status-${i}`}>{tx(status(slot))}</span>
      </div>
      {slot ? (
        <>
          <button onClick={onOpen} className="mv-checker flex h-24 items-center justify-center overflow-hidden rounded-lg" data-testid={`studio-slot-open-${i}`}><img src={slot.originalUrl} alt="" className="max-h-full max-w-full object-contain" /></button>
          <p className="mt-1.5 truncate text-[11px]" title={slot.name}>{slot.name}</p>
          <p className="text-[10px] text-[var(--mv-dim)]">{slot.meta.w} × {slot.meta.h}px · {fmtBytes(slot.meta.bytes)}</p>
          <div className="mt-2 flex gap-3 text-[11px]">
            <button onClick={() => ref.current.click()} className="flex items-center gap-1 text-[var(--mv-teal)]" data-testid={`studio-slot-replace-${i}`}><RefreshCw className="h-3 w-3" />{tx("Replace")}</button>
            <button onClick={onRemove} className="flex items-center gap-1 text-red-400" data-testid={`studio-slot-remove-${i}`}><X className="h-3 w-3" />{tx("Remove")}</button>
          </div>
        </>
      ) : (
        <div className="flex flex-1 flex-col items-center justify-center gap-2 text-center">
          <button onClick={() => ref.current.click()} className="flex flex-col items-center gap-1.5" data-testid={`studio-slot-upload-${i}`}>
            <UploadCloud className="h-7 w-7 text-[var(--mv-teal)]" /><span className="text-xs font-semibold">{tx("Upload image to crop")}</span>
            <span className="text-[10px] text-[var(--mv-dim)]">{tx("Click or drag & drop · PNG, JPG, WebP · max 15MB")}</span>
          </button>
          <button onClick={onGenerate} className="flex items-center gap-1 text-[11px] text-[var(--mv-teal)]" data-testid={`studio-slot-generate-${i}`}><Sparkles className="h-3 w-3" />{tx("or generate an original image")}</button>
        </div>
      )}
      <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={(e) => { const f = e.target.files[0]; e.target.value = ""; if (f) onFile(f); }} data-testid={`studio-slot-input-${i}`} />
    </div>
  );
};

export function ImageStudio() { useLang();
  const [slots, setSlots] = useState([null, null, null]);
  const [active, setActive] = useState(null);
  const [gen, setGen] = useState(null);
  const lib = useLibrary();
  const update = (i, patch) => setSlots((s) => s.map((x, k) => (k === i ? { ...x, ...patch } : x)));
  const load = async (i, f, ai = []) => {
    if (!OK.includes(f.type)) { toast.error(tx("Only PNG, JPG or WebP images are allowed")); return; }
    if (f.size > 15 * 1024 * 1024) { toast.error(tx("Image must be under 15MB")); return; }
    try {
      const url = URL.createObjectURL(f); const im = await loadImage(url);
      const meta = { w: im.naturalWidth, h: im.naturalHeight, bytes: f.size, type: f.type.split("/")[1].toUpperCase() };
      setSlots((s) => s.map((x, k) => (k === i ? freshSlot(i, toCanvas(im), { file: f, name: f.name, originalUrl: url, meta, ai }) : x)));
      setActive(i); setGen(null);
    } catch (e) { toast.error(tx("This file could not be read as an image")); }
  };
  const remove = (i) => { setSlots((s) => s.map((x, k) => (k === i ? null : x))); if (active === i) setActive(null); };
  const slot = active != null ? slots[active] : null;
  const unsaved = slots.some((x) => x && (!x.saved || x.saved.stale));
  useEffect(() => {
    if (!unsaved) return;
    const h = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", h); return () => window.removeEventListener("beforeunload", h);
  }, [unsaved]);
  return (
    <section className="mt-8 space-y-4" data-testid="image-studio">
      <div><h3 className="font-display text-lg font-bold">{tx("AI Image Crop Studio")}</h3>
        <p className="text-xs text-[var(--mv-dim)]">{tx("Upload → crop / AI cutout → preview → download or SAVE to your image library. Nothing here changes the homepage.")}</p></div>
      <div className="grid gap-3 sm:grid-cols-3">
        {slots.map((s, i) => <SlotCard key={i} i={i} slot={s} active={active === i} onFile={(f) => load(i, f)} onOpen={() => setActive(i)} onRemove={() => remove(i)} onGenerate={() => { setGen(i); setActive(null); }} />)}
      </div>
      {gen != null && <GeneratePanel onCancel={() => setGen(null)} onDone={(f) => load(gen, f, ["generate"])} />}
      {slot && (
        <div className="rounded-2xl border border-[var(--mv-border)] bg-[var(--mv-card)] p-4">
          {slot.stage === "edit" && <Editor slot={slot} update={(p) => update(active, p)} onReview={() => update(active, { stage: "review" })} />}
          <ResultPanel slot={slot} update={(p) => update(active, p)} onSaved={lib.load} />
        </div>
      )}
      <div className="rounded-2xl border border-[var(--mv-border)] bg-[var(--mv-card)] p-4" data-testid="image-library">
        <p className="mb-3 text-xs font-bold uppercase tracking-wider">{tx("MIVO image library")}</p>
        <LibraryGrid items={lib.items} onArchive={lib.archive} />
      </div>
    </section>
  );
}
